import { DateTimeEditor, DropdownEditor, Field, NumberEditor, TextAreaEditor, TextEditor } from '@approvalmax/ui';
import { Grid } from '@approvalmax/ui/src/components';
import { miscHelpers } from '@approvalmax/utils';
import { useAtomValue } from 'jotai';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import { SectionContainer } from 'pages/requestForm/components/SectionContainer';
import { FC, useCallback, useEffect, useState } from 'react';
import { usePrevious } from 'react-use';
import { useNetSuiteInvoiceFieldSettings } from 'shared/hooks';

import { getNetSuiteContext } from '../../../../../selectors/netsuite/netSuiteSelectors';
import {
    NetSuiteCurrencySection,
    NetSuiteCustomersDataProvider,
    NetSuiteCustomFields,
    NetSuiteDiscountItemsDataProvider,
} from '../../../components';
import { NetSuiteExtendedCustomer } from '../../../data/types';
import { useCurrencyPrecision, useNetSuiteInvoiceItemList } from '../../../hooks';
import {
    isFieldEditable,
    isFieldMandatory,
    isFieldReadOnly,
    isFieldVisible,
    isOptionalFieldInvalid,
} from '../../../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { netSuiteShowErrorsState } from '../../../resources/states';
import { isDiscountRateValid } from '../../NetSuiteInvoiceRequestForm.helpers';
import { useNetSuiteInvoiceFields } from '../../NetSuiteInvoiceRequestForm.hooks';
import { netSuiteInvoiceFieldValuesState } from '../../NetSuiteInvoiceRequestForm.states';
import { NetSuiteInvoiceFields } from '../../NetSuiteInvoiceRequestForm.types';
import { messages } from './FieldsSection.messages';
import { FieldsSectionProps } from './FieldsSection.types';

const { netSuiteConstants } = constants;

export const FieldsSection: FC<FieldsSectionProps> = (props) => {
    const { workflowId, discountAmount, discountDate, customerData, isFetchingBillingAddresses } = props;

    const netSuiteEditingContext = useSelector(getNetSuiteContext);
    const showErrors = useAtomValue(netSuiteShowErrorsState);
    const fieldValues = useAtomValue(netSuiteInvoiceFieldValuesState);

    const currencyPrecision = useCurrencyPrecision(fieldValues.currency);

    const { documentFields, currencies } = netSuiteEditingContext;

    const { onFieldChange, updateFieldsBulk } = useNetSuiteInvoiceFields();

    const { updateItemListOnCurrencyChange } = useNetSuiteInvoiceItemList();

    const fieldSettings = useNetSuiteInvoiceFieldSettings(documentFields);

    const previousCustomerData = usePrevious(customerData);

    const [shouldUpdateCustomerData, setShouldUpdateCustomerData] = useState(false);

    useEffect(() => {
        if (shouldUpdateCustomerData && customerData && previousCustomerData?.id !== customerData?.id) {
            setShouldUpdateCustomerData(false);

            const { currency } = customerData;

            if (currency?.id !== fieldValues.currency?.id) {
                updateItemListOnCurrencyChange(currency);
            }

            updateFieldsBulk({
                currency: customerData.currency ?? null,
                leadSource: customerData.leadSource ?? null,
                salesRep: customerData.salesRep ?? null,
                partner: customerData.partner ?? null,
                billingAddress: customerData.addresses.find(({ isDefaultBilling }) => isDefaultBilling) ?? null,
                shippingAddress: customerData.addresses.find(({ isDefaultShipping }) => isDefaultShipping) ?? null,
                vatRegistrationNumber: customerData.taxNumber ?? null,
            });
        }
    }, [
        shouldUpdateCustomerData,
        previousCustomerData,
        customerData,
        fieldValues.currency,
        updateFieldsBulk,
        updateItemListOnCurrencyChange,
    ]);

    const onEditorChange = useCallback(
        <K extends keyof NetSuiteInvoiceFields>(key: K) =>
            (value: NetSuiteInvoiceFields[K]) =>
                onFieldChange(key, value),
        [onFieldChange]
    );

    const onCustomerChange = useCallback(
        (customer: NetSuiteExtendedCustomer | null) => {
            updateFieldsBulk({
                customer,
                currency: null,
                leadSource: null,
                salesRep: null,
                partner: null,
                billingAddress: null,
                shippingAddress: null,
                vatRegistrationNumber: null,
            });

            setShouldUpdateCustomerData(true);
        },
        [updateFieldsBulk]
    );

    const onCurrencyChange = useCallback(
        (currency: domain.NetSuiteCurrency | null) => {
            updateItemListOnCurrencyChange(currency);

            updateFieldsBulk({
                currency,
                exchangeRate: null,
            });
        },
        [updateItemListOnCurrencyChange, updateFieldsBulk]
    );

    return (
        <>
            <SectionContainer title={messages.customerDetailsSectionTitle}>
                <Grid gridTemplateColumns='repeat(auto-fill, 267px)' rowGap={24} columnGap={32} padding='0 0 24'>
                    <Field title={messages.customerFieldText} required>
                        <NetSuiteCustomersDataProvider
                            integrationCode={domain.IntegrationCode.NetSuiteInvoice}
                            subsidiaryId={fieldValues.subsidiary?.id}
                        >
                            <DropdownEditor
                                value={fieldValues.customer || null}
                                singleLinePerItem={false}
                                onChange={onCustomerChange}
                                placeholder={messages.customerFieldPlaceholder}
                                invalid={showErrors && !fieldValues.customer}
                            />
                        </NetSuiteCustomersDataProvider>
                    </Field>

                    {isFieldVisible(fieldSettings.billingAddress) && (
                        <Field
                            title={messages.billingAddressFieldText}
                            required={isFieldMandatory(fieldSettings.billingAddress)}
                        >
                            <PlainDataProvider items={customerData?.addresses ?? []}>
                                <DropdownEditor
                                    value={fieldValues.billingAddress || null}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('billingAddress')}
                                    placeholder={
                                        isFieldEditable(fieldSettings.billingAddress)
                                            ? messages.billingAddressPlaceholder
                                            : ''
                                    }
                                    invalid={isOptionalFieldInvalid(
                                        showErrors,
                                        fieldValues.billingAddress,
                                        fieldSettings.billingAddress
                                    )}
                                    displayAttribute='fullAddress'
                                    loading={isFetchingBillingAddresses}
                                    disabled={isFieldReadOnly(fieldSettings.billingAddress)}
                                />
                            </PlainDataProvider>
                        </Field>
                    )}

                    {isFieldVisible(fieldSettings.vatRegistration) && (
                        <Field
                            title={messages.vatRegistrationFieldText}
                            required={isFieldMandatory(fieldSettings.vatRegistration)}
                        >
                            <TextEditor
                                value={fieldValues.vatRegistrationNumber ?? ''}
                                onChange={onEditorChange('vatRegistrationNumber')}
                                placeholder={messages.textFieldPlaceholder}
                                invalid={isOptionalFieldInvalid(
                                    showErrors,
                                    fieldValues.vatRegistrationNumber,
                                    fieldSettings.vatRegistration
                                )}
                                disabled={isFieldReadOnly(fieldSettings.vatRegistration)}
                            />
                        </Field>
                    )}
                </Grid>
            </SectionContainer>

            <NetSuiteCurrencySection
                currencies={currencies}
                fieldValues={fieldValues}
                fieldSettings={fieldSettings}
                onExchangeRateChange={onEditorChange('exchangeRate')}
                onCurrencyChange={onCurrencyChange}
            />

            <SectionContainer title={messages.invoiceDetailsSectionTitle}>
                <Grid gridTemplateColumns='repeat(auto-fill, 267px)' rowGap={24} columnGap={32} padding='0 0 24'>
                    {isFieldVisible(fieldSettings.transactionNumber) && (
                        <Field title={messages.transactionNumberFieldText}>
                            <TextEditor
                                value={fieldValues.transactionNumber || ''}
                                onChange={onEditorChange('transactionNumber')}
                                disabled
                            />
                        </Field>
                    )}

                    {isFieldVisible(fieldSettings.reference) && (
                        <Field title={messages.transactionReferenceFieldText}>
                            <TextEditor
                                value={fieldValues.transactionReference || ''}
                                placeholder={
                                    isFieldEditable(fieldSettings.reference) ? messages.textFieldPlaceholder : ''
                                }
                                maxLength={netSuiteConstants.referenceMaxLength}
                                invalid={isOptionalFieldInvalid(
                                    showErrors,
                                    fieldValues.transactionReference,
                                    fieldSettings.reference
                                )}
                                onChange={onEditorChange('transactionReference')}
                                changeOnBlur
                            />
                        </Field>
                    )}

                    <Field title={messages.transactionDateFieldText} required>
                        <DateTimeEditor
                            value={fieldValues.transactionDate}
                            placeholder={messages.dateFieldPlaceholder}
                            invalid={showErrors && !fieldValues.transactionDate}
                            onChange={onEditorChange('transactionDate')}
                        />
                    </Field>

                    {isFieldVisible(fieldSettings.dueDate) && (
                        <Field title={messages.dueDateFieldText}>
                            <DateTimeEditor
                                value={fieldValues.dueDate}
                                placeholder={
                                    isFieldEditable(fieldSettings.dueDate) ? messages.dateFieldPlaceholder : ''
                                }
                                invalid={isOptionalFieldInvalid(showErrors, fieldValues.dueDate, fieldSettings.dueDate)}
                                onChange={onEditorChange('dueDate')}
                            />
                        </Field>
                    )}

                    {isFieldVisible(fieldSettings.startDate) && (
                        <Field title={messages.startDateFieldText} required={isFieldMandatory(fieldSettings.startDate)}>
                            <DateTimeEditor
                                value={fieldValues.startDate}
                                placeholder={
                                    isFieldEditable(fieldSettings.startDate) ? messages.dateFieldPlaceholder : ''
                                }
                                disabled={isFieldReadOnly(fieldSettings.startDate)}
                                invalid={isOptionalFieldInvalid(
                                    showErrors,
                                    fieldValues.startDate,
                                    fieldSettings.startDate
                                )}
                                onChange={onEditorChange('startDate')}
                            />
                        </Field>
                    )}

                    {isFieldVisible(fieldSettings.endDate) && (
                        <Field title={messages.endDateFieldText} required={isFieldMandatory(fieldSettings.endDate)}>
                            <DateTimeEditor
                                value={fieldValues.endDate}
                                placeholder={
                                    isFieldEditable(fieldSettings.endDate) ? messages.dateFieldPlaceholder : ''
                                }
                                disabled={isFieldReadOnly(fieldSettings.endDate)}
                                invalid={isOptionalFieldInvalid(showErrors, fieldValues.endDate, fieldSettings.endDate)}
                                onChange={onEditorChange('endDate')}
                            />
                        </Field>
                    )}

                    {isFieldVisible(fieldSettings.terms) && (
                        <Field title={messages.termsFieldText} required={isFieldMandatory(fieldSettings.terms)}>
                            <PlainDataProvider items={netSuiteEditingContext.terms}>
                                <DropdownEditor
                                    value={fieldValues.terms || null}
                                    singleLinePerItem={false}
                                    placeholder={
                                        isFieldEditable(fieldSettings.terms) ? messages.termsFieldPlaceholder : ''
                                    }
                                    onChange={onEditorChange('terms')}
                                    disabled={isFieldReadOnly(fieldSettings.terms)}
                                    invalid={isOptionalFieldInvalid(showErrors, fieldValues.terms, fieldSettings.terms)}
                                />
                            </PlainDataProvider>
                        </Field>
                    )}

                    <Field title={messages.discountAmountFieldText}>
                        <NumberEditor
                            allowFloat
                            value={discountAmount}
                            onChange={miscHelpers.noop}
                            precision={currencyPrecision}
                            placeholder=''
                            displayFormat='currency'
                            disabled
                        />
                    </Field>

                    {isFieldVisible(fieldSettings.discountDate) && (
                        <Field title={messages.discountDateFieldText}>
                            <DateTimeEditor value={discountDate} placeholder='' onChange={miscHelpers.noop} disabled />
                        </Field>
                    )}

                    {isFieldVisible(fieldSettings.class) && (
                        <Field title={messages.classFieldText} required={isFieldMandatory(fieldSettings.class)}>
                            <PlainDataProvider items={netSuiteEditingContext.classes}>
                                <DropdownEditor
                                    value={fieldValues.class}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('class')}
                                    placeholder={
                                        isFieldEditable(fieldSettings.class) ? messages.classFieldPlaceholder : ''
                                    }
                                    invalid={isOptionalFieldInvalid(showErrors, fieldValues.class, fieldSettings.class)}
                                    disabled={isFieldReadOnly(fieldSettings.class)}
                                    loading={false}
                                />
                            </PlainDataProvider>
                        </Field>
                    )}

                    {isFieldVisible(fieldSettings.department) && (
                        <Field
                            title={messages.departmentFieldText}
                            required={isFieldMandatory(fieldSettings.department)}
                        >
                            <PlainDataProvider items={netSuiteEditingContext.departments}>
                                <DropdownEditor
                                    value={fieldValues.department}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('department')}
                                    placeholder={
                                        isFieldEditable(fieldSettings.department)
                                            ? messages.departmentFieldPlaceholder
                                            : ''
                                    }
                                    invalid={isOptionalFieldInvalid(
                                        showErrors,
                                        fieldValues.department,
                                        fieldSettings.department
                                    )}
                                    disabled={isFieldReadOnly(fieldSettings.department)}
                                    loading={false}
                                />
                            </PlainDataProvider>
                        </Field>
                    )}

                    {isFieldVisible(fieldSettings.location) && (
                        <Field title={messages.locationFieldText} required={isFieldMandatory(fieldSettings.location)}>
                            <PlainDataProvider items={netSuiteEditingContext.locations}>
                                <DropdownEditor
                                    value={fieldValues.location}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('location')}
                                    placeholder={
                                        isFieldEditable(fieldSettings.location) ? messages.locationFieldPlaceholder : ''
                                    }
                                    invalid={isOptionalFieldInvalid(
                                        showErrors,
                                        fieldValues.location,
                                        fieldSettings.location
                                    )}
                                    disabled={isFieldReadOnly(fieldSettings.location)}
                                    loading={false}
                                />
                            </PlainDataProvider>
                        </Field>
                    )}

                    <Field title={messages.postingPeriodFieldText}>
                        <PlainDataProvider items={[]}>
                            <DropdownEditor
                                value={fieldValues.postingPeriod}
                                onChange={onEditorChange('postingPeriod')}
                                placeholder=''
                                disabled
                            />
                        </PlainDataProvider>
                    </Field>

                    {isFieldVisible(fieldSettings.discountItem) && (
                        <Field
                            title={messages.discountItemFieldText}
                            required={isFieldMandatory(fieldSettings.discountItem)}
                        >
                            <NetSuiteDiscountItemsDataProvider>
                                <DropdownEditor
                                    value={fieldValues.discountItem}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('discountItem')}
                                    placeholder={
                                        isFieldEditable(fieldSettings.discountItem)
                                            ? messages.discountItemFieldPlaceholder
                                            : ''
                                    }
                                    invalid={
                                        isOptionalFieldInvalid(
                                            showErrors,
                                            fieldValues.discountItem,
                                            fieldSettings.discountItem
                                        ) ||
                                        (showErrors && !!fieldValues.discountRate?.trim() && !fieldValues.discountItem)
                                    }
                                    disabled={isFieldReadOnly(fieldSettings.discountItem)}
                                    loading={false}
                                />
                            </NetSuiteDiscountItemsDataProvider>
                        </Field>
                    )}

                    {isFieldVisible(fieldSettings.discountRate) && (
                        <Field
                            title={messages.discountRateFieldText}
                            required={isFieldMandatory(fieldSettings.discountRate)}
                        >
                            <TextEditor
                                value={fieldValues.discountRate || ''}
                                placeholder={
                                    isFieldEditable(fieldSettings.discountRate) ? messages.textFieldPlaceholder : ''
                                }
                                maxLength={netSuiteConstants.discountRateMaxLength}
                                onChange={onEditorChange('discountRate')}
                                disabled={isFieldReadOnly(fieldSettings.discountRate)}
                                invalid={
                                    isOptionalFieldInvalid(
                                        showErrors,
                                        fieldValues.discountRate,
                                        fieldSettings.discountRate
                                    ) ||
                                    (showErrors && !isDiscountRateValid(fieldValues.discountRate))
                                }
                                changeOnBlur
                            />
                        </Field>
                    )}

                    <NetSuiteCustomFields workflowId={workflowId} showErrors={showErrors} noFieldMargins />
                </Grid>
            </SectionContainer>

            {isFieldVisible(fieldSettings.memo) && (
                <SectionContainer title={messages.memoSectionTitle}>
                    <Grid gridTemplateColumns='565px'>
                        <TextAreaEditor
                            value={fieldValues.memo || ''}
                            onChange={onEditorChange('memo')}
                            placeholder={isFieldEditable(fieldSettings.memo) ? messages.textFieldPlaceholder : ''}
                            changeOnBlur
                            minHeight={5}
                            maxLength={netSuiteConstants.memoMaxLength}
                            invalid={isOptionalFieldInvalid(showErrors, fieldValues.memo, fieldSettings.memo)}
                            disabled={isFieldReadOnly(fieldSettings.memo)}
                        />
                    </Grid>
                </SectionContainer>
            )}
        </>
    );
};

FieldsSection.displayName = 'FieldsSection';
